import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Alert,
  Box,
  Flex,
  LightModeSemanticColors,
  PrimaryButton,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
  TertiaryButton,
  Text,
  VStack,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownToBracketRegular } from '@enechain/ecloud-icons'

import { HintTooltip } from '~/components/common/HintTooltip'
import { Modal } from '~/components/common/Modal'
import { iconSize } from '~/constants/iconSize'

// chakra v3.29 + React 18 型定義の既知不具合 (DemandForecastWorkspace.tsx / SpotBidding/
// components/tabs/BlockBidTab.tsx と同じ workaround) で RadioGroupItem* の children /
// value / disabled が型上欠落するため cast する。
const RadioGroupItem = RawRadioGroupItem as React.FC<
  HTMLChakraProps<'div'> & { value: string; disabled?: boolean }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<
  HTMLChakraProps<'span'>
>

type CsvFormat = 'base' | 'peak' | 'offpeak' | 'slot48'

type Props = {
  isOpen: boolean
  onClose: () => void
  onDownload: (format: CsvFormat) => void
  areaLabels: string[]
  periodLabel: string
  /** §4-3 / docs/specs/supply/supply-demand-gap.md L142: 48 点 (30 分粒度) は指定日
   *  モードのみが仕様。 design HTML 側は範囲を問わず常に選択可だが (design/spec drift)、
   *  spec が明示的に指定日限定と定めているため本実装は spec を優先しゲートを維持する。 */
  isSlot48Available: boolean
}

export const CsvDownloadModal = ({
  isOpen,
  onClose,
  onDownload,
  areaLabels,
  periodLabel,
  isSlot48Available,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [format, setFormat] = useState<CsvFormat>('base')

  // slot48 は指定日 range のみ (spec 由来、上記 isSlot48Available 参照)。 range が変わって
  // 48 点が無効化されたら、直前に選んでいた slot48 を base に戻す (= 無効な選択のまま
  // download するとファイル名と粒度が矛盾するため)。
  useEffect(() => {
    if (!isSlot48Available && format === 'slot48') {
      setFormat('base')
    }
  }, [isSlot48Available, format])

  const formatOptions: Array<{
    value: CsvFormat
    label: string
    description: string
  }> = [
    {
      value: 'base',
      label: t('page:supplyGap.csv.formatBase'),
      description: t('page:supplyGap.csv.formatBaseTitle'),
    },
    {
      value: 'peak',
      label: t('page:supplyGap.csv.formatPeak'),
      description: t('page:supplyGap.csv.formatPeakTitle'),
    },
    {
      value: 'offpeak',
      label: t('page:supplyGap.csv.formatOffpeak'),
      description: t('page:supplyGap.csv.formatOffpeakTitle'),
    },
    {
      value: 'slot48',
      label: t('page:supplyGap.csv.format48'),
      description: t('page:supplyGap.csv.format48Title'),
    },
  ]

  const showCoefNote = format === 'peak' || format === 'offpeak'

  // フッターのボタンは DS 既定サイズ (md)。 モーダルの footer に sm を当てない
  // (2026-09-16 デザイナー指摘「modal のボタンも DS 違反」。 8080 `supply/supply-gap.html`
  // の CSV モーダルも `btn-sm` を付けていない)。
  const footer = (
    <Flex gap={2}>
      <TertiaryButton onClick={onClose}>
        {t('page:supplyGap.csv.cancel')}
      </TertiaryButton>
      <PrimaryButton onClick={() => onDownload(format)}>
        <FaArrowDownToBracketRegular boxSize={iconSize.labeledButton} />
        {t('page:supplyGap.csv.download')}
      </PrimaryButton>
    </Flex>
  )

  return (
    // 器は共通の Modal (DS Dialog)。 幅は既定の md = min(560px, 94vw)
    // (UI 辞書 components.md `.ecl-modal`。 8080 の CSV モーダルも既定幅)。
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      title={t('page:supplyGap.csv.title')}
    >
      {/* 出力粒度: 縦並びの実ラジオ。 各選択肢の説明は画面に常時出さず、 選択肢の
          ツールチップで出す (spec supply-demand-gap.md AC-SDG-013 と付録の
          「出力粒度「ベース」のツールチップ」 等)。 */}
      <Box marginBottom={4}>
        <Text as="p" textStyle="captionStrong" marginBottom={2}>
          {t('page:supplyGap.csv.formatLabel')}
        </Text>
        <RadioGroupRoot
          aria-label={t('page:supplyGap.csv.formatLabel')}
          onValueChange={(details: { value: string | null }) => {
            if (details.value !== null) {
              setFormat(details.value as CsvFormat)
            }
          }}
          value={format}
        >
          <VStack alignItems="flex-start" gap={2}>
            {formatOptions.map((opt) => {
              const disabled = opt.value === 'slot48' && !isSlot48Available
              return (
                <HintTooltip key={opt.value} content={opt.description}>
                  <RadioGroupItem disabled={disabled} value={opt.value}>
                    <RadioGroupItemHiddenInput />
                    <RadioGroupItemControl />
                    <RadioGroupItemText>{opt.label}</RadioGroupItemText>
                  </RadioGroupItem>
                </HintTooltip>
              )
            })}
          </VStack>
        </RadioGroupRoot>
      </Box>

      {/* 対象情報 */}
      <div
        style={{
          padding: '12px',
          background: LightModeSemanticColors.Surface.secondary.value,
          // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (既存 raw style。D-1 の系統 1 本化で解消予定)
          borderRadius: '4px',
          fontSize: fontSizes.md.value,
        }}
      >
        <div style={{ marginBottom: '4px' }}>
          {t('page:supplyGap.csv.targetAreas')}:{' '}
          <strong>{areaLabels.join(', ')}</strong>
        </div>
        <div>
          {t('page:supplyGap.csv.targetPeriod')}: <strong>{periodLabel}</strong>
        </div>
      </div>

      {/* ピーク/オフピーク選択時のみ info alert (design L228-235 準拠)。
          TODO(#1362): 出力粒度は現状ファイル名にのみ反映され、 CSV の中身は表示中の
          代表値・期間データをそのまま出力する (粒度別の行生成・厳密な時間帯係数変換は
          #1362 で対応)。 その現状に合わせ、 design の「ベース値からの概算」文言をそのまま
          使わず「表示中データをそのまま出力する」旨に置き換えている。 */}
      {showCoefNote && (
        <Alert
          marginTop={2}
          description={t('page:supplyGap.csv.coefNoteDescription')}
          status="info"
          title={t('page:supplyGap.csv.coefNoteTitle')}
        />
      )}
    </Modal>
  )
}
